import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GiftCard } from '~models';
import { environment } from '../../../environments/environment';
import { constants } from '../../app.constants';
import {
  EStaticCategory,
  GiftcardCategoryService,
} from 'src/app/shared-data/giftcard-category.service';
import Fuse from 'fuse.js';

@Injectable()
export class GiftCardsService {
  private giftCards: GiftCard[] = [];
  private staticCategory = EStaticCategory;

  constructor(
    private ngHttp: HttpClient,
    public giftcardCategoryService: GiftcardCategoryService,
  ) {}

  async getCards(enableVendor = null): Promise<GiftCard[]> {
    if (!this.giftCards.length) {
      let url = `${environment.apiHost}/giftcards`;

      const params = new URLSearchParams();
      params.set('hideDisabled', 'true');

      if (enableVendor) {
        params.set('enable', enableVendor);
      }

      url += `?${params.toString()}`;

      const response = await this.ngHttp.get<GiftCard[]>(url).toPromise();
      this.giftCards = response;
    }

    return this.giftCards;
  }

  async getFeaturedCards(): Promise<string[]> {
    const url = `${environment.apiHost}/featuredCards?hideDisabled=true`;
    let featuredCards = constants.featured;

    try {
      const featuredCardsResult = await this.ngHttp.get<string[]>(url).toPromise();

      featuredCards = featuredCardsResult || featuredCards;
    } catch (error) {
      return featuredCards;
    }
    return featuredCards;
  }

  search(query: string): GiftCard[] {
    if (!query.length) {
      return undefined;
    }

    return this.getCardsByKeyword(query);
  }

  getCardsByKeyword(query: string): GiftCard[] {
    if (!query.length) {
      return undefined;
    }

    // First, we perform a fuzzy search based on brand and keywords properties
    const fuzzySearchOptions: Fuse.IFuseOptions<GiftCard> = {
      threshold: 0.4,
      ignoreLocation: true,
      keys: ['brand', 'keywords'],
    };

    const fuzzySearchFuse = new Fuse(this.giftCards, fuzzySearchOptions);

    const fuzzySearchResult = fuzzySearchFuse.search(query);

    // Second, as the importantContent is a huge string, fuzzy search on this produces a very fuzzy result
    // Therefore, we perform an exact search on this property.
    const exactSearchOptions: Fuse.IFuseOptions<GiftCard> = {
      threshold: 0.1,
      ignoreLocation: true,
      keys: ['importantContent'],
    };

    const exactSearchFuse = new Fuse(
      this.giftCards.filter((gc) => !fuzzySearchResult.map((f) => f.item.id).includes(gc.id)),
      exactSearchOptions,
    );

    const exactSearchResult = exactSearchFuse.search(query);

    // Finally, combine the results from the 2 sets of search.
    return [...fuzzySearchResult, ...exactSearchResult].map((sr) => sr.item);
  }

  async getCard(id) {
    try {
      const giftCard = await this.ngHttp
        .get<GiftCard>(`${environment.apiHost}/giftcards/${id}?hideDisabled=true`)
        .toPromise();
      if (!giftCard) {
        const error = new Error(`Card not found with the id: "${id}"`);

        throw error;
      }

      return giftCard;
    } catch (error) {
      throw error;
    }
  }

  async getCardCategories(): Promise<string[]> {
    const url = `${environment.apiHost}/category/list`;
    let allCategories = [];

    try {
      allCategories = await this.ngHttp.get<string[]>(url).toPromise();
      this.giftcardCategoryService.setDataGiftcardCategories(allCategories);
    } catch (error) {
      return error;
    }

    return allCategories;
  }

  async getCardCategoryById(categoryId: string): Promise<string[]> {
    if (!(Object.values(this.staticCategory) as string[]).includes(categoryId)) {
      this.giftcardCategoryService.setStateLoadingGiftcardCategory(true);

      const url = `${environment.apiHost}/category/${categoryId}`;
      let cards;
      let cardsFilterCategory = [];

      try {
        cards = await this.ngHttp.get<string[]>(url).toPromise();
        cards = cards.result.cardIds;

        cardsFilterCategory = this.giftCards.filter(
          (card) => cards.find((cardCategory) => cardCategory === card.id) !== undefined,
        );

        this.giftcardCategoryService.setDataGiftcardCategoriesFiltering(cardsFilterCategory);
      } catch (error) {
        return error;
      }

      this.giftcardCategoryService.setStateLoadingGiftcardCategory(false);

      return cardsFilterCategory;
    }
  }
}
